/* Pure Shenandoah -- shared accordion motion (buy box AND product FAQs).

   Ported from the source's `.product-header1_accordion`, sampled frame by frame
   off live: the panel animates height 0 <-> auto and the chevron rotates
   0 <-> 180deg over 380ms with plain `ease` -- Webflow IX2's default curve.

   Fit duration and easing TOGETHER against the raw samples, and stamp t0 in the
   same frame you dispatch the trigger. Driving the click through the test
   driver adds tens of ms of latency that lands entirely in the start offset;
   reading a midpoint under that offset made this look like easeOutCubic, which
   the joint fit ranks last (rms 0.129 against 0.008 for `ease`). The tell is
   the first tenth: live is at 0.101 progress at u=0.1, essentially linear,
   where easeOutCubic is already at 0.271.

   An ARIA disclosure rather than `<details>`: Chrome keeps a laid-out box for a
   closed `<details>`'s content, so `getBoundingClientRect()` on the panel
   returns its full height while collapsed and a height animation has no
   distance to travel. Measured directly -- the panel read 539 both open and
   closed, and the icon was the only thing that moved.

   Section-scoped, idempotent, keyboard-operable, and reduced-motion aware. */
/* The `customElements.define` below was already guarded, but these two consts
   were not, and the product page loads this file TWICE -- once from the
   `custom_liquid` block in templates/product.json that renders the buy-box
   accordion, and once from sections/product-faqs.liquid. Two identical
   `<script src>` tags execute twice, so the second run threw
   "Identifier 'DURATION' has already been declared" and aborted before reaching
   the guard. Uncaught on every product view; surfaced by overflow-check.mjs's
   pageerror hook. Block-scoping them inside the guard makes a repeat include a
   no-op, which also covers a page that renders both `product-faqs` and
   `pure-faq-list`. */
if (!customElements.get('pure-accordion')) {
  const DURATION = 380;
  const EASING = 'ease';

  customElements.define(
    'pure-accordion',
    class PureAccordion extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;

        // Queried by data attribute so the FAQ band can share this controller and
        // therefore this exact 380ms `ease` -- the two accordions on the product
        // page have to move identically. The class fallbacks keep an older
        // buy-box markup revision working if the two files sync out of step.
        this.button = this.querySelector('[data-accordion-summary], .pure-buy-tab__summary');
        this.panel = this.querySelector('[data-accordion-panel], .pure-buy-tab__panel');
        // A custom element upgrades as soon as its OPEN tag is parsed, so if this
        // script is ever loaded without `defer` the children do not exist yet.
        // Retry on the next frame rather than silently binding nothing.
        if (!this.button || !this.panel) {
          requestAnimationFrame(() => this.connectedCallback());
          return;
        }
        this.dataset.bound = 'true';

        this.animation = null;
        // The markup ships expanded so it degrades without JS; collapse it now.
        this.#setExpanded(this.dataset.open === 'true');

        this.onClick = this.#toggle.bind(this);
        this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
        if (this.animation) this.animation.cancel();
        this.dataset.bound = 'false';
      }

      get #reduced() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      get #expanded() {
        return this.button.getAttribute('aria-expanded') === 'true';
      }

      #setExpanded(open) {
        this.button.setAttribute('aria-expanded', String(open));
        this.panel.hidden = !open;
      }

      #toggle() {
        const open = !this.#expanded;
        // Capture the painted height BEFORE cancelling an interrupted motion.
        // scrollHeight is the full content height, so it jumps on reversal.
        const from = this.panel.hidden ? 0 : this.panel.getBoundingClientRect().height;
        const previous = this.animation;
        this.animation = null;
        previous?.cancel();

        if (this.#reduced) {
          this.panel.style.overflow = '';
          this.#setExpanded(open);
          return;
        }

        this.panel.hidden = false;
        this.button.setAttribute('aria-expanded', String(open));
        const to = open ? this.panel.getBoundingClientRect().height : 0;
        this.#run(from, to, open);
      }

      #run(from, to, open) {
        this.panel.style.overflow = 'hidden';
        // `cancel()` dispatches `oncancel` ASYNCHRONOUSLY, so a toggle that
        // interrupts a running animation queues the old handler and then starts a
        // new one. Without the identity guard below, that stale handler lands mid
        // way through the REPLACEMENT animation and clears `overflow`, so the
        // panel keeps painting its full text while its box shrinks -- the "text
        // stays behind the close" bug -- and nulls `this.animation`, so the next
        // toggle has nothing left to cancel.
        const animation = this.panel.animate(
          { height: [`${from}px`, `${to}px`] },
          { duration: DURATION, easing: EASING, fill: 'both' }
        );
        this.animation = animation;
        const settle = () => {
          if (this.animation !== animation) return; // superseded -- leave it alone
          this.animation = null;
          // Commit visibility before removing the final keyframe. Closing must
          // never expose the natural full height for a frame at completion.
          this.panel.hidden = !open;
          animation.cancel();
          this.panel.style.overflow = '';
        };
        animation.onfinish = settle;
        animation.oncancel = settle;
      }
    }
  );
}
